import { useIntl } from "react-intl";
import { Schema, FieldType } from "../../../toolympus/hooks/useSchema";
import { useLoadedData } from "../../../toolympus/hooks/useLoadedData";
import { useTextFilter } from "../../../toolympus/components/schemed/Filtering/useTextFilter";
import { useEditItem2, useNewItem, withActionOnSaveItem } from "../../../toolympus/api/useNewItem";
import { useItemActionWithConfirmation } from "../../../toolympus/api/useAction";
import { apiFetch } from "../../../toolympus/api/core";


export interface CopConference {
  _id: number;
  title: string;
  description?: any;
  color?: string | null;
  image_url?: string | null;
  sort_order: number;
  is_hidden?: boolean;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
}

export const CopConferenceSchema: Schema = {
  _id: { label: "ID", type: FieldType.number },
  sort_order: { label: "Sort order", type: FieldType.number },
  title: { label: "Title" },
  description: { label: "Description", type: FieldType.powerdoc },
  color: { label: "Color", type: FieldType.color },
  image_url: { label: "Image URL", type: FieldType.mediafile },
  is_hidden: { label: "Hidden", type: FieldType.bool },
  created_by: { label: "Created by", dictionary: "Users", type: FieldType.dictionarySelect },
  created_at: { label: "Created at", type: FieldType.datetime },
  updated_by: { label: "Updated by", dictionary: "Users", type: FieldType.dictionarySelect },
  updated_at: { label: "Updated at", type: FieldType.datetime },
};

const ApiPath = "/api/cop-decision/tree/conference";


export const useCopConferences = () => {
  const data = useLoadedData<CopConference[]>(ApiPath, []);
  const filter = useTextFilter<CopConference>(c => `${c.title} ${c._id}`);

  const newConference = useNewItem<Omit<CopConference, "_id">, CopConference>(
    ApiPath,
    {
      title: "",
      sort_order: Math.max(...data.data.map(c => c.sort_order ?? 0), 0) + 1,
      is_hidden: false,
      color: null,
      image_url: null,
      description: null,
    }
  );

  const editConference = useEditItem2<CopConference>({
    getApiPath: c => `${ApiPath}/${c._id}`,
  });

  const { formatMessage } = useIntl();

  const removeConference = useItemActionWithConfirmation<CopConference, {}>(
    item =>
      apiFetch<CopConference>(`${ApiPath}/${item?._id}`, "delete").then(x => {
        data.reload();
        return x;
      }),
    {
      title: formatMessage({ id: "dictionaries.remove_title" }),
      confirmationHint: formatMessage({ id: "dictionaries.remove_hint" }),
    }
  );

  return {
    ...data,
    data: filter.filterData(data.data),
    filter,
    schema: CopConferenceSchema,

    newConference: withActionOnSaveItem(newConference, x => { data.reload() }),
    editConference: withActionOnSaveItem(editConference, x => { data.reload() }),
    removeConference,
  };
};

export type ManageCopConferencesData = ReturnType<typeof useCopConferences>;

export const updateCopConference = (id: number, changes: Partial<CopConference>) => {
  return apiFetch<CopConference>(`${ApiPath}/${id}`, "put", changes);
};
